/**
 * Shared "General Note" step. The four non-intake forms (general,
 * case-management, clinical-follow-up, cocm-follow-up) and the legacy
 * read-only form all render an identical comment textarea. Centralised
 * here so each typed form's body is one line instead of fifteen.
 *
 * @agentic connections
 */

import { Textarea } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { FormField } from "@benchmark/ui/components/atomic";

/**
 * Form shapes that own a `comment` field.
 *
 * @agentic connections
 */
type CommentCapableFormData = {
  comment?: string;
};

/**
 * Props for {@link GeneralNoteStep}.
 *
 * @agentic connections
 */
export type GeneralNoteStepProps<T extends CommentCapableFormData> = {
  form: UseFormReturnType<T>;
  /** When `true`, marks the field required and adds a red asterisk. */
  required?: boolean;
};

/**
 * Renders the General Note textarea inside an encounter form's Notes step.
 *
 * @agentic connections
 */
export const GeneralNoteStep = <T extends CommentCapableFormData>({
  form,
  required = true,
}: GeneralNoteStepProps<T>) => {
  const error = form.errors["comment"] as string | undefined;
  return (
    <FormField
      label="General Note"
      variant="section"
      showDivider={false}
      description="Document the key points of this interaction."
      required={required}
      error={error}
    >
      <Textarea
        error={error ? <></> : undefined}
        value={form.values.comment ?? ""}
        onChange={(e) =>
          form.setFieldValue("comment" as never, e.target.value as never)
        }
        autosize
        minRows={3}
        placeholder="Document the key points of this interaction."
      />
    </FormField>
  );
};
